/** XeAgents.ai - Multi-Tenant Context Provider **/
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

// Feature flags interface
export interface TenantFeatures {
  // Core features (always enabled for all plans)
  voiceAgents: boolean;
  phoneNumbers: boolean;
  incomingCalls: boolean;
  callRecording: boolean;
  callTransfer: boolean;
  basicCRM: boolean;
  // Premium features (toggleable per tenant)
  campaigns: boolean;
  flowBuilder: boolean;
  aiFlowGeneration: boolean;
  knowledgeBase: boolean;
  webhooks: boolean;
  calendarIntegration: boolean;
  recruitment: boolean;
  // Enterprise features
  customBranding: boolean;
  ssoEnabled: boolean;
  customModules: boolean;
}

// Custom module interface
export interface CustomModule {
  key: string;
  displayName: string;
  description: string | null;
  icon: string | null;
  route: string;
  enabled: boolean;
  config: any;
}

// Tenant branding interface
export interface TenantBranding {
  companyName: string | null;
  tagline: string | null;
  logoUrl: string | null;
  logoUrlDark: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  termsUrl: string | null;
  privacyUrl: string | null;
  customCss: string | null;
}

// Full tenant config interface
export interface TenantConfig {
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: 'simple' | 'standard' | 'enterprise';
    domain: string | null;
    isActive: boolean;
  } | null;
  branding: TenantBranding;
  features: TenantFeatures;
  customModules: CustomModule[];
}

// Context state interface
interface TenantContextState {
  config: TenantConfig | null;
  isLoading: boolean;
  error: Error | null;
  tenantSlug: string;
  isEnterprise: boolean;
  refetch: () => void;
}

// Default features for standard plan
const DEFAULT_FEATURES: TenantFeatures = {
  voiceAgents: true,
  phoneNumbers: true,
  incomingCalls: true,
  callRecording: true,
  callTransfer: true,
  basicCRM: true,
  campaigns: true,
  flowBuilder: true,
  aiFlowGeneration: true,
  knowledgeBase: true,
  webhooks: true,
  calendarIntegration: true,
  recruitment: false, // Requires admin enablement per organization
  customBranding: false,
  ssoEnabled: false,
  customModules: false,
};

// Default branding
const DEFAULT_BRANDING: TenantBranding = {
  companyName: null,
  tagline: null,
  logoUrl: null,
  logoUrlDark: null,
  faviconUrl: null,
  primaryColor: null,
  secondaryColor: null,
  supportEmail: null,
  supportPhone: null,
  termsUrl: null,
  privacyUrl: null,
  customCss: null,
};

// Create context
const TenantContext = createContext<TenantContextState | undefined>(undefined);

/**
 * Extract tenant subdomain from current URL
 *
 * URL Pattern: {tenant}.xevoicepro.xenonlabs.ai
 *
 * Examples:
 *   elite.xevoicepro.xenonlabs.ai -> 'elite'
 *   acme.xevoicepro.xenonlabs.ai -> 'acme'
 *   xevoicepro.xenonlabs.ai -> 'default' (main app, no tenant)
 *   localhost:3000 -> 'default' (development)
 *   localhost:3000?tenant=elite -> 'elite' (development override)
 */
function extractTenantSlug(): string {
  const hostname = window.location.hostname;

  // Development: Check for ?tenant= query parameter or localStorage override
  if (hostname === 'localhost' || hostname.match(/^[\d.]+$/)) {
    // Check URL query parameter: ?tenant=elite
    const urlParams = new URLSearchParams(window.location.search);
    const tenantParam = urlParams.get('tenant');

    // If tenant parameter is explicitly in URL
    if (tenantParam !== null) {
      if (tenantParam === '' || tenantParam === 'default') {
        // Clear stored tenant when accessing ?tenant= or ?tenant=default
        localStorage.removeItem('dev_tenant_override');
        return 'default';
      }
      // Store in localStorage for persistence across page loads
      localStorage.setItem('dev_tenant_override', tenantParam);
      return tenantParam.toLowerCase();
    }

    // If NO tenant parameter in URL, clear stored tenant and use default
    // This ensures http://localhost:5000/ always shows default, not org-specific flow
    localStorage.removeItem('dev_tenant_override');
    return 'default';
  }

  // Parse the hostname parts
  const parts = hostname.split('.');

  // Check for tenant subdomain pattern: {tenant}.xevoicepro.xenonlabs.ai (4 parts)
  if (parts.length >= 4) {
    const subdomain = parts[0].toLowerCase();
    const productDomain = parts[1].toLowerCase();

    // Verify it's the xevoicepro product domain
    if (productDomain === 'xevoicepro') {
      // 'app', 'www', 'api' are reserved - treat as default
      if (subdomain === 'app' || subdomain === 'www' || subdomain === 'api') {
        return 'default';
      }
      return subdomain;
    }
  }

  // Also support simpler pattern: {tenant}.xenonlabs.ai (3 parts)
  if (parts.length === 3) {
    const subdomain = parts[0].toLowerCase();
    // 'xevoicepro', 'app', 'www', 'api' are treated as default
    if (subdomain === 'xevoicepro' || subdomain === 'app' || subdomain === 'www' || subdomain === 'api') {
      return 'default';
    }
    return subdomain;
  }

  return 'default';
}

/**
 * TenantProvider component
 * Wraps the app and provides tenant configuration to all children
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const tenantSlug = extractTenantSlug();
  const [cssApplied, setCssApplied] = useState(false);

  // Fetch tenant config from API
  const { data: config, isLoading, error, refetch } = useQuery<TenantConfig>({
    queryKey: ["/api/tenant/config", tenantSlug],
    queryFn: async () => {
      const response = await fetch("/api/tenant/config", {
        headers: {
          "X-Tenant-Subdomain": tenantSlug,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to load tenant configuration");
      }
      return response.json();
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    retry: 2,
  });

  // Apply tenant-specific CSS variables when branding changes
  useEffect(() => {
    if (config?.branding) {
      const { primaryColor, secondaryColor, customCss } = config.branding;

      // Apply primary color as CSS variable
      if (primaryColor) {
        document.documentElement.style.setProperty('--tenant-primary', primaryColor);
        // Convert hex to HSL for Tailwind compatibility
        const hsl = hexToHSL(primaryColor);
        if (hsl) {
          document.documentElement.style.setProperty('--primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
        }
      }

      // Apply secondary color
      if (secondaryColor) {
        document.documentElement.style.setProperty('--tenant-secondary', secondaryColor);
      }

      // Apply custom CSS
      if (customCss) {
        let styleEl = document.getElementById('tenant-custom-css');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'tenant-custom-css';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = customCss;
      }

      setCssApplied(true);
    }
  }, [config?.branding]);

  // Apply favicon when branding changes
  useEffect(() => {
    if (config?.branding?.faviconUrl) {
      const existingFavicon = document.querySelector("link[rel='icon']");
      if (existingFavicon) {
        existingFavicon.setAttribute("href", config.branding.faviconUrl);
      } else {
        const favicon = document.createElement("link");
        favicon.rel = "icon";
        favicon.href = config.branding.faviconUrl;
        document.head.appendChild(favicon);
      }
    }
  }, [config?.branding?.faviconUrl]);

  // Update document title with tenant name
  useEffect(() => {
    if (config?.branding?.companyName) {
      const currentTitle = document.title;
      // Only update if it doesn't already contain the company name
      if (!currentTitle.includes(config.branding.companyName)) {
        document.title = `${config.branding.companyName}`;
      }
    }
  }, [config?.branding?.companyName]);

  const contextValue: TenantContextState = {
    config: config || null,
    isLoading,
    error: error as Error | null,
    tenantSlug,
    isEnterprise: config?.tenant?.plan === 'enterprise',
    refetch,
  };

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access the full tenant context
 */
export function useTenant(): TenantContextState {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

/**
 * Hook to check if a specific feature is enabled for the current tenant
 */
export function useFeature(featureKey: keyof TenantFeatures): boolean {
  const { config } = useTenant();
  if (!config?.features) {
    return DEFAULT_FEATURES[featureKey];
  }
  return config.features[featureKey] ?? DEFAULT_FEATURES[featureKey];
}

/**
 * Hook to get all enabled features
 */
export function useFeatures(): TenantFeatures {
  const { config } = useTenant();
  return config?.features || DEFAULT_FEATURES;
}

/**
 * Hook to get tenant branding
 */
export function useTenantBranding(): TenantBranding {
  const { config } = useTenant();
  return config?.branding || DEFAULT_BRANDING;
}

/**
 * Hook to get custom modules for the current tenant
 */
export function useCustomModules(): CustomModule[] {
  const { config } = useTenant();
  if (!config?.customModules) {
    return [];
  }
  return config.customModules.filter(m => m.enabled);
}

/**
 * Hook to check if current tenant is enterprise
 */
export function useIsEnterprise(): boolean {
  const { isEnterprise } = useTenant();
  return isEnterprise;
}

/**
 * Hook to check if current tenant matches a specific slug
 *
 * Usage:
 *   const isElite = useIsTenant('elite');
 *   if (isElite) {
 *     // Elite-specific UI
 *   }
 */
export function useIsTenant(tenantSlug: string): boolean {
  const { tenantSlug: currentSlug } = useTenant();
  return currentSlug === tenantSlug;
}

/**
 * Hook to check if current tenant is any of the specified slugs
 *
 * Usage:
 *   const isVendingClient = useIsAnyTenant(['elite', 'vendmax']);
 */
export function useIsAnyTenant(tenantSlugs: string[]): boolean {
  const { tenantSlug } = useTenant();
  return tenantSlugs.includes(tenantSlug);
}

/**
 * Hook to get tenant-specific config value
 *
 * Usage:
 *   const apiEndpoint = useTenantConfig({
 *     'elite': 'https://elite-api.com',
 *     'acme': 'https://acme-api.com',
 *     'default': null,
 *   });
 */
export function useTenantConfig<T>(configs: Record<string, T>): T {
  const { tenantSlug } = useTenant();

  if (tenantSlug in configs) {
    return configs[tenantSlug];
  }

  return configs['default'];
}

/**
 * Component wrapper that only renders for specific tenants
 *
 * Usage:
 *   <ForTenant tenants={['elite']}>
 *     <EliteSpecificComponent />
 *   </ForTenant>
 */
export function ForTenant({
  tenants,
  children,
  fallback = null,
}: {
  tenants: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { tenantSlug } = useTenant();

  if (tenants.includes(tenantSlug)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * Helper: Convert hex color to HSL
 */
function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  if (hex.length !== 6) {
    return null;
  }

  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}
